import React from "react";
import { Star } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getHotelReviews } from "@/lib/hotelReviews";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || obj.es || "";
};

// Small deterministic pool of Amazigh-tinted background swatches so
// cards feel varied but calm inside the narrow text column.
const CARD_BG = ["bg-[#FDFBF7]", "bg-[#FAF6EE]", "bg-[#F5EDE0]"];

// Compact initials fallback for the avatar disc when the author name is
// long — "Anne-Sophie M." → "AM"; "The Vermeer family" → "TV".
const initialsOf = (author) => {
  const words = (author || "").replace(/[.&,]/g, "").split(/\s+/).filter(Boolean);
  if (!words.length) return "•";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const StarRow = ({ rating, testId }) => (
  <div
    className="inline-flex items-center gap-0.5"
    role="img"
    aria-label={`${rating} out of 5 stars`}
    data-testid={testId}
  >
    {[1, 2, 3, 4, 5].map((i) => {
      const filled = i <= rating;
      return (
        <Star
          key={`star-${i}`}
          className={`w-3.5 h-3.5 ${filled ? "text-[#C16542] fill-[#C16542]" : "text-[#2C2621]/20"}`}
          strokeWidth={1.5}
          aria-hidden="true"
        />
      );
    })}
  </div>
);

const ReviewCard = ({ review, lang, index }) => {
  const bg = CARD_BG[index % CARD_BG.length];
  return (
    <li
      className={`${bg} border border-[#2C2621]/10 p-5 md:p-6 rounded-sm`}
      data-testid={`hotel-review-${review.id}`}
    >
      <div className="flex items-center gap-3">
        <span
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#2C2621] text-[#FDFBF7] text-[11px] tracking-[0.14em] font-medium shrink-0"
          aria-hidden="true"
          data-testid={`hotel-review-${review.id}-avatar`}
        >
          {initialsOf(review.author)}
        </span>
        <div className="min-w-0">
          <p className="font-serif-x text-base md:text-lg leading-tight text-[#2C2621] truncate" data-testid={`hotel-review-${review.id}-author`}>
            {review.author}
          </p>
          <p className="text-[11px] tracking-[0.12em] uppercase text-[#5C5248] mt-0.5" data-testid={`hotel-review-${review.id}-origin`}>
            {pick(review.origin, lang)}
            {review.date ? <span aria-hidden="true"> · </span> : null}
            {review.date ? <span className="normal-case tracking-normal text-[#5C5248]/80">{pick(review.date, lang)}</span> : null}
          </p>
        </div>
        <div className="ml-auto shrink-0">
          <StarRow rating={review.rating} testId={`hotel-review-${review.id}-stars`} />
        </div>
      </div>
      <blockquote
        className="mt-4 text-[15px] md:text-base leading-relaxed text-[#2C2621]/85 italic"
        data-testid={`hotel-review-${review.id}-quote`}
      >
        “{pick(review.quote, lang)}”
      </blockquote>
    </li>
  );
};

// Renders inline inside the "About the Hotel" text column, below the
// founder quote. Uses whatever slug the hotel provides — falls back to
// a small on-brand default set when the slug is unknown.
const HotelReviews = ({ hotelSlug, headingKey }) => {
  const { lang, t } = useLanguage();
  const reviews = getHotelReviews(hotelSlug);
  if (!reviews || reviews.length === 0) return null;

  // Cap at 4 to keep the column readable — the site's "Wall of Love"
  // page carries the full catalogue for guests who want to read more.
  const items = reviews.slice(0, 4);
  const avg = (items.reduce((s, r) => s + (Number(r.rating) || 0), 0) / items.length).toFixed(1);

  const heading = headingKey ? t(headingKey) : pick({
    en: "Reviews from our guests",
    fr: "Avis de nos hôtes",
    es: "Opiniones de nuestros clientes",
  }, lang);

  const summaryLabel = pick({
    en: `${avg}/5 · ${items.length} verified reviews`,
    fr: `${avg}/5 · ${items.length} avis vérifiés`,
    es: `${avg}/5 · ${items.length} opiniones verificadas`,
  }, lang);

  return (
    <section
      className="mt-12"
      data-testid={`hotel-reviews-${hotelSlug || "default"}`}
      data-cms-scope={hotelSlug ? `hotel.${hotelSlug}.reviews` : "hotel.reviews"}
    >
      <div className="flex items-baseline flex-wrap gap-x-4 gap-y-1">
        <h3
          className="font-serif-x text-2xl md:text-[28px] leading-tight text-[#2C2621]"
          data-testid="hotel-reviews-heading"
        >
          {heading}
        </h3>
        <p className="text-[11px] tracking-[0.22em] uppercase text-[#5C5248]" data-testid="hotel-reviews-summary">
          {summaryLabel}
        </p>
      </div>
      <ul className="mt-5 space-y-4" data-testid="hotel-reviews-list">
        {items.map((review, i) => (
          <ReviewCard key={review.id} review={review} lang={lang} index={i} />
        ))}
      </ul>
    </section>
  );
};

export default HotelReviews;
